import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useVessel } from '../contexts/VesselContext';
import { usePermissions } from './usePermissions';
import { getDeletionReasons } from '../config/auditReasons';
import { saveAuditLog, loadAuditLog, appendAuditEntry, combinedAuditKey } from '../utils/auditStorage';
import { generateAuditHash, sortEntriesByTimestamp } from '../utils/hashUtils';
import DeleteReasonModal from '../components/common/DeleteReasonModal';

/**
 * Universal Audit Trail Hook
 *
 * Provides audit trail capabilities for any module.
 * Handles deletion with reason capture, change logging, and restoration.
 *
 * Design principles:
 * - Module-agnostic: works with equipment, crew, defects, incidents, etc.
 * - Soft delete first: items are flagged, not removed (30-day restore window)
 * - Full audit trail: captures who, what, when, why
 * - Future-proof: ready for indexedDB or cloud sync
 *
 * @param {string} moduleName - Name of the module using this hook
 * @returns {Object} Audit trail functions and state
 */
export const useAuditTrail = (moduleName) => {
  const { currentVesselId, getCurrentVessel } = useVessel();
  const { currentUser } = usePermissions();
  const currentVessel = getCurrentVessel();

  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    item: null,
    onConfirm: null
  });

  // FT-515 fix (2026-09-29): loadAuditLog is now async — it routes through
  // storageRouter, which is IndexedDB in production. Mirror useStorageState's
  // pattern (useEffect + async storage read + local state hydration) instead
  // of the old synchronous useMemo. `getChanges` below reads synchronously
  // from this hydrated `entries` state, so callers don't need to change their
  // call sites, but the state itself now arrives asynchronously and its
  // identity change (via `entries`) triggers a re-render once the read
  // resolves.
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const requestIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const requestId = ++requestIdRef.current;
    setIsLoading(true);

    loadAuditLog(moduleName, currentVesselId)
      .then((loaded) => {
        if (cancelled || requestId !== requestIdRef.current) return;
        setEntries(Array.isArray(loaded) ? loaded : []);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled || requestId !== requestIdRef.current) return;
        setEntries([]);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [moduleName, currentVesselId, reloadToken]);

  // Re-hydrate entries from storage (used after a successful logChange so
  // viewers reflect the new entry immediately instead of waiting for the
  // hook to remount).
  const refreshEntries = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  // FT-515 round 3: react to a LATER background write to the combined
  // audit_log key, not just this hook's own initial load.
  //
  // Round 2 fixed the case where hydration completes within THIS hook's own
  // initial async read (getChanges' identity changes once `entries` updates,
  // and the viewer's useMemo depends on getChanges). It did NOT cover a
  // background update that lands AFTER that initial read has already
  // resolved — e.g. a cloud sync pull for audit_log that completes seconds
  // after the screen opened and its own (empty) local read already finished.
  // In that case `entries` stays frozen on the stale snapshot for the
  // lifetime of the mount; nothing re-runs the load effect.
  //
  // Mirror useStorageState.js's pattern (see its `sr:sync:hydrate` /
  // `sr:storage:set` listeners) instead of inventing a new mechanism: both
  // events are already dispatched by the storage layer on every write to the
  // combined audit key (auditStorage.js's saveAuditLog/appendAuditEntry route
  // through storageRouter.writeJSON, and UniversalSyncBridge dispatches
  // sr:sync:hydrate on every cloud pull for the audit_log collection). When a
  // matching event fires, call refreshEntries() — this bumps reloadToken,
  // which re-runs the load effect above, which sets isLoading back to true
  // for the duration of the re-read. That's what gives viewers (e.g.
  // VesselSettingsHistoryView) a real loading indicator during the re-fetch
  // instead of leaving them stuck on a stale "No changes" empty state.
  useEffect(() => {
    if (!currentVesselId) return undefined;

    const auditKey = combinedAuditKey(currentVesselId);

    // Cloud sync pull landed (UniversalSyncBridge.js handleCloudUpdate).
    // detail.module is always the literal base key 'audit_log' (the combined
    // key has no per-module segment — see auditStorage.js's combinedAuditKey
    // comment) and detail.vesselId is the vessel the pull was for. audit_log
    // is not in ORG_SCOPED_MODULES, so vesselId filtering applies.
    const handleHydrate = (event) => {
      const detail = event.detail || {};
      if (detail.module !== 'audit_log') return;
      if (detail.vesselId !== currentVesselId) return;
      refreshEntries();
    };

    // Same-tab write to the combined audit key completing async (e.g. the
    // storageRouter.writeJSON inside saveAuditLog/appendAuditEntry, or the
    // cloud-hydrate write UniversalSyncBridge itself performs before
    // dispatching sr:sync:hydrate). detail.key is the exact key string
    // (storageIndexedDB.writeJSON passes the key through unchanged), so
    // match it directly against the same key auditStorage.js reads/writes.
    const handleStorageSet = (event) => {
      const detail = event.detail || {};
      if (detail.key !== auditKey) return;
      refreshEntries();
    };

    window.addEventListener('sr:sync:hydrate', handleHydrate);
    window.addEventListener('sr:storage:set', handleStorageSet);
    return () => {
      window.removeEventListener('sr:sync:hydrate', handleHydrate);
      window.removeEventListener('sr:storage:set', handleStorageSet);
    };
  }, [currentVesselId, refreshEntries]);

  /**
   * Generate unique audit entry ID
   */
  const generateAuditId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    return `audit_${timestamp}_${random}`;
  };

  /**
   * Format timestamp with both ISO and local time
   */
  const formatTimestamp = (date = new Date()) => {
    return {
      iso: date.toISOString(),
      local: date.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  /**
   * Calculate retention date based on regulatory requirements
   * @param {string} moduleName - Module name
   * @param {string} action - Action type
   * @returns {string|null} ISO date for retention deadline
   */
  const calculateRetentionDate = (moduleName, action) => {
    const now = new Date();
    let yearsToAdd = 0;

    // Retention periods from WBC3 / MCA regulations
    switch (moduleName) {
      case 'equipment':
      case 'maintenance':
        yearsToAdd = 5; // WBC3 3.5.2
        break;
      case 'drills':
        yearsToAdd = 2; // MGN 71
        break;
      case 'incidents':
        yearsToAdd = 5; // MSN 1853
        break;
      case 'crew':
        yearsToAdd = 2; // MSN 1865 (after employment ends)
        break;
      case 'defects':
        yearsToAdd = 2; // After defect closed
        break;
      case 'voyages':
        yearsToAdd = 5; // FT-269: ISM Code record retention + MGN 478 official log book retention
        break;
      default:
        yearsToAdd = 2; // Default 2 years
    }

    // Soft deletions: 30 days restore window + retention period
    if (action === 'delete') {
      now.setDate(now.getDate() + 30); // 30-day restore window first
    }

    now.setFullYear(now.getFullYear() + yearsToAdd);
    return now.toISOString();
  };

  /**
   * Log a change to the audit trail
   * v4.2 Days 16-17: Now async for hash-chaining
   *
   * @param {Object} params - Change parameters
   * @param {string} params.action - Action type (delete, create, update, archive, restore)
   * @param {Object} params.item - Item being changed
   * @param {Object} params.reason - Reason object { code, label, notes }
   * @param {string} params.authorizedBy - Optional authorizer name
   * @param {string} params.deleteType - 'soft' or 'hard' for deletions
   * @param {Object} params.changes - Optional before/after data for updates
   * @param {Object} params.moduleSpecificData - Optional module-specific fields
   */
  const logChange = useCallback(async (params) => {
    const {
      action,
      item,
      reason = null,
      authorizedBy = null,
      deleteType = 'soft',
      changes = null,
      moduleSpecificData = null
    } = params;

    const timestamp = formatTimestamp();
    const restoreDeadline = action === 'delete' && deleteType === 'soft'
      ? new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)).toISOString() // 30 days
      : null;

    // v4.2 Days 16-17: Get previous hash for chain
    const existingEntries = await loadAuditLog(moduleName, currentVesselId);
    const sortedEntries = sortEntriesByTimestamp(existingEntries);
    const previousHash = sortedEntries.length > 0
      ? sortedEntries[sortedEntries.length - 1].hash
      : null;

    const entry = {
      id: generateAuditId(),
      module: moduleName,
      action,
      deleteType: action === 'delete' ? deleteType : null,
      timestamp: timestamp.iso,
      timestampLocal: timestamp.local,
      user: {
        id: currentUser?.id || 'unknown',
        name: currentUser?.name || 'Unknown User',
        role: currentUser?.role || 'unknown'
      },
      authorizedBy: authorizedBy || null,
      vesselId: currentVesselId,
      vesselName: currentVessel?.name || currentVessel?.vesselName || 'Unknown Vessel',
      item: {
        id: item.id,
        type: item.type || moduleName,
        name: item.name || item.label || item.type || 'Unknown',
        category: item.category || null,
        location: item.location || null,
        serialNumber: item.serialNumber || item.serialNo || null
      },
      reason,
      snapshot: { ...item }, // Full item data for restoration
      restoreDeadline,
      changes, // For updates: { before: {}, after: {} }

      // v4.2 Days 16-17: Module-specific data
      moduleSpecificData: moduleSpecificData || null,

      // v4.2 Days 16-17: Hash-chaining fields
      previousHash: previousHash,
      hash: null, // Will be calculated below

      // v4.2 Days 16-17: Retention policy
      retentionUntil: calculateRetentionDate(moduleName, action)
    };

    // v4.2 Days 16-17: Generate hash BEFORE saving (await, not .then)
    entry.hash = await generateAuditHash(entry);

    // Save to storage
    const success = await appendAuditEntry(moduleName, currentVesselId, entry);

    if (!success) {
      console.error('[AUDIT] Failed to save audit entry');
    } else {
      // FT-515: re-hydrate local state so ChangeLogViewer / RAHistoryViewer
      // reflect the new entry immediately.
      refreshEntries();
    }

    return success;
  }, [moduleName, currentVesselId, currentVessel, currentUser, refreshEntries]);

  /**
   * Get all audit log entries for this module
   *
   * @param {Object} filters - Optional filters
   * @param {string} filters.action - Filter by action type
   * @param {string} filters.itemId - Filter by specific item ID
   * @param {Date} filters.startDate - Filter by start date
   * @param {Date} filters.endDate - Filter by end date
   * @returns {Array} Filtered audit log entries
   */
  const getChanges = useCallback((filters = {}) => {
    // FT-515: reads from the already-hydrated `entries` state (see the
    // useEffect above) instead of calling the now-async loadAuditLog
    // directly — keeps this a synchronous call for existing callers.
    let filtered = entries;

    // Apply filters
    if (filters.action) {
      filtered = filtered.filter(e => e.action === filters.action);
    }
    if (filters.itemId) {
      filtered = filtered.filter(e => e.item.id === filters.itemId);
    }
    if (filters.startDate) {
      filtered = filtered.filter(e => new Date(e.timestamp) >= filters.startDate);
    }
    if (filters.endDate) {
      filtered = filtered.filter(e => new Date(e.timestamp) <= filters.endDate);
    }

    return filtered;
  }, [entries]);

  /**
   * Show delete dialog with reason capture
   *
   * @param {Object} item - Item to delete
   * @param {function} onConfirm - Callback after confirmation with reason
   * @returns {Promise} Resolves with reason data or null if cancelled
   */
  const showDeleteDialog = useCallback((item, onConfirm) => {
    return new Promise((resolve) => {
      setDeleteModalState({
        isOpen: true,
        item,
        onConfirm: (reasonData) => {
          setDeleteModalState({ isOpen: false, item: null, onConfirm: null });
          if (onConfirm) {
            onConfirm(reasonData);
          }
          resolve(reasonData);
        },
        onCancel: () => {
          setDeleteModalState({ isOpen: false, item: null, onConfirm: null });
          resolve(null);
        }
      });
    });
  }, []);

  /**
   * Restore a soft-deleted item
   *
   * @param {string} auditEntryId - Audit entry ID of the deletion
   * @returns {Object|null} Snapshot of deleted item, or null if not found/expired
   */
  const restoreItem = useCallback((auditEntryId) => {
    const entries = getChanges({ action: 'delete' });
    const entry = entries.find(e => e.id === auditEntryId);

    if (!entry) {

      return null;
    }

    if (entry.deleteType === 'hard') {

      return null;
    }

    // Check if restore deadline has passed
    if (entry.restoreDeadline && new Date() > new Date(entry.restoreDeadline)) {

      return null;
    }

    // Log the restore action
    logChange({
      action: 'restore',
      item: entry.snapshot,
      reason: {
        code: 'restored',
        label: 'Item restored from deletion',
        notes: `Restored from deletion on ${entry.timestampLocal}`
      }
    });

    return entry.snapshot;
  }, [getChanges, logChange]);

  /**
   * Get deletion reasons for this module
   */
  const reasons = useMemo(() => getDeletionReasons(moduleName), [moduleName]);

  /**
   * Check if user can delete items
   */
  const canDelete = useMemo(() => {
    // FT-254 (2026-05-31): added super_admin. Previously only fleet + skipper had
    // delete capability, which meant super_admin saw NO trash button at all —
    // a paradox where a higher-privilege role had less power than a lower one.
    // Handle both 'super_admin' and legacy 'SUPER_ADMIN' (uppercase) — both
    // variants are present in the codebase (see getOrgId.js:50).
    const role = currentUser?.role;
    return role === 'fleet'
      || role === 'skipper'
      || role === 'super_admin'
      || role === 'SUPER_ADMIN';
  }, [currentUser]);

  /**
   * Check if user can perform hard delete
   */
  const canHardDelete = useMemo(() => {
    // FT-254 (2026-05-31): added super_admin (both case variants) alongside
    // fleet. Hard delete (permanent removal, no restore) remains scoped to
    // the two highest-privilege roles. Skipper can still archive but not
    // permanently delete.
    const role = currentUser?.role;
    return role === 'fleet' || role === 'super_admin' || role === 'SUPER_ADMIN';
  }, [currentUser]);

  /**
   * Delete Modal Component
   */
  const DeleteModal = useCallback(() => (
    <DeleteReasonModal
      isOpen={deleteModalState.isOpen}
      onClose={deleteModalState.onCancel}
      onConfirm={deleteModalState.onConfirm}
      item={deleteModalState.item || {}}
      module={moduleName}
      reasons={reasons}
      allowHardDelete={canHardDelete}
    />
  ), [deleteModalState, moduleName, reasons, canHardDelete]);

  return {
    // Core functions
    logChange,
    getChanges,
    showDeleteDialog,
    restoreItem,

    // Utilities
    reasons,
    canDelete,
    canHardDelete,

    // FT-515: true while the initial (or a post-write refresh) async storage
    // read is in flight — lets viewers show a loading state instead of a
    // premature "no entries" empty state.
    isLoading,

    // Components
    DeleteModal
  };
};
